import { IconButton, MenuItem, Tooltip, Typography } from '@mui/material';
import { Done } from '@mui/icons-material';
import React, {
  MouseEvent as ContentMenuEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PresetMenuItemProps } from './types';
import usePresetItemStyles from './styles';
import PresetDialog from '../PresetDialog';

const PresetItem: React.FunctionComponent<PresetMenuItemProps> = (props) => {
  const classes = usePresetItemStyles();
  const { t } = useTranslation(['video', 'common']);
  const {
    preset,
    index,
    handleClosePresetMenu,
    savePreset,
    gotoPreset,
    cameraName,
    hasConfigurePrivilege,
    hasUpdatePrivilege,
    hasCreatePrivilege,
  } = props;
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [showEditItem, setShowEditItem] = useState(false);
  const presetItemRef = useRef<any>(null);

  const handleClickOutsideItem = useCallback(
    (event: MouseEvent) => {
      !showPresetDialog &&
        presetItemRef.current &&
        !presetItemRef.current.contains(event.target) &&
        setShowEditItem(false);
    },
    [showPresetDialog]
  );

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutsideItem);
    return () => {
      window.removeEventListener('mousedown', handleClickOutsideItem);
    };
  }, [handleClickOutsideItem, presetItemRef]);

  const hasPrivilege = useMemo(() => {
    if (preset.isSystem) {
      return hasConfigurePrivilege;
    }

    return hasUpdatePrivilege;
  }, [hasConfigurePrivilege, hasUpdatePrivilege, preset]);

  const commonProps = useMemo(() => {
    return {
      onClick: () => {
        handleClosePresetMenu();
        setShowEditItem(false);
        gotoPreset?.(preset.id);
      },
      onContextMenu: (event: ContentMenuEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setShowEditItem(true);
      },
    };
  }, [gotoPreset, handleClosePresetMenu, preset.id]);

  const renderEditPresetDialog = useCallback(() => {
    return (
      <PresetDialog
        open={showPresetDialog}
        handleClosePresetDialog={() => {
          setShowPresetDialog(false);
          handleClosePresetMenu();
          setShowEditItem(false);
        }}
        preset={preset}
        savePreset={savePreset}
        cameraName={cameraName}
        hasConfigurePrivilege={hasConfigurePrivilege}
        hasUpdatePrivilege={hasUpdatePrivilege}
        hasCreatePrivilege={hasCreatePrivilege}
      />
    );
  }, [
    cameraName,
    handleClosePresetMenu,
    hasConfigurePrivilege,
    hasCreatePrivilege,
    hasUpdatePrivilege,
    preset,
    savePreset,
    showPresetDialog,
  ]);

  const renderPresetMenuItem = () => {
    return (
      <div
        className={clsx(classes.presetItemWrapper, { [classes.selectedPresetItem]: showEditItem })}
        ref={presetItemRef}
      >
        {renderEditPresetDialog()}
        <MenuItem
          {...commonProps}
          href='#'
          key={`presetItem_${index}`}
          classes={{
            root: classes.presetItemRoot,
          }}
        >
          <div
            className={clsx(classes.systemIconWrapper, { [classes.hiddenDiv]: !preset.isSystem })}
          >
            <Tooltip title={t('video:systemPreset')}>
              <IconButton
                classes={{ root: classes.systemIconRoot }}
                size='small'
                className={classes.systemIcon}
              >
                <Typography fontSize='smaller' variant='subtitle2'>
                  s
                </Typography>
              </IconButton>
            </Tooltip>
          </div>
          <Typography variant='subtitle2' className={classes.presetItemText}>
            {preset.name}
          </Typography>
          <Tooltip
            title={t('video:selectedPreset')}
            className={clsx({ [classes.hiddenDiv]: !preset.isSelected })}
          >
            <IconButton
              className={clsx(classes.selectedPresetIcon, {
                [classes.hiddenDiv]: !preset.isSelected,
                [classes.wideSelectedPresetIcon]: !showEditItem,
              })}
              size='small'
            >
              <Done fontSize='small' />
            </IconButton>
          </Tooltip>
        </MenuItem>
        {showEditItem && (
          <MenuItem
            disabled={!hasPrivilege}
            classes={{ root: classes.editPresetItemRoot }}
            onClick={() => {
              hasPrivilege && setShowPresetDialog(true);
              setShowEditItem(false);
            }}
          >
            <Typography className={classes.editText} fontSize='smaller' variant='subtitle2'>
              {t('common:edit')}
            </Typography>
          </MenuItem>
        )}
      </div>
    );
  };

  return renderPresetMenuItem();
};

export default PresetItem;
